import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import { formatAmount } from '../../../utils/formatAmount';
import useRefundTransfer from './useRefundTransfer';
import useGetCustomerProfile from '../../../hooks/useGetCustomerProfile';
import ProfileCombo from '../../../sharedComponents/ProfileCombo';
import { BANKS, REASONS, BankLogoButton, PRODUCT_PAYMENT_STYLE } from './refundShared';
import { computeComponents, itemAmount, sumRefundTotal } from './refundAmount';

const modalStyles = {
  overlay: { backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 1000 },
  content: {
    top: '5%', left: '50%', right: 'auto', bottom: 'auto',
    width: '960px', maxWidth: '95vw', maxHeight: '90vh',
    transform: 'translateX(-50%)', overflowY: 'auto', overflowX: 'hidden',
  },
};

const emptyItem = () => ({ url: '', orderId: null, isManual: false, amount: '', note: '' });

// The exact charge sums each installment/component at satang (e.g. two 50% halves + fee +
// down fee), while the estimate rounds the combined product once — so a few-satang gap is
// normal rounding, not a mistake. Only flag when they differ by more than that; a real
// discrepancy (wrong rate/amount, a missed component) shows up far larger.
// Compare in whole satang: as floats, 100.12 - 100.07 = 0.0500000004 > 0.05, so an
// exactly-5-satang gap wrongly showed the ⚠️ estimate badge (QA 803).
const amtDiffers = (a, b) => Math.abs(Math.round(((a || 0) - (b || 0)) * 100)) > 5;

const emptyForm = () => ({
  customerId: null,
  smileName: '',
  amount: '',
  productLinks: [emptyItem()],
  bankName: '',
  accountNumber: '',
  accountName: '',
  reason: '',
  reasonNote: '',
  keepAsCredit: false,
});

const RefundCreateModal = ({ isOpen, onClose, onCreated, prefill = null, lockCustomer = false }) => {
  const [form, setForm] = useState(emptyForm());
  const formRef = useRef(form);
  formRef.current = form; // always the latest links, for the same-URL lookup below
  const [linkStatuses, setLinkStatuses] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const lookupTimers = useRef({});

  const { createRecord, searchCustomer, searchedProfile, setSearchedProfile, searchByUrl } = useRefundTransfer();
  const { profiles, getCustomerProfiles } = useGetCustomerProfile();

  // The selected customer's full profile from the loaded list — reliable for both the
  // search and the cancel-prefill paths (searchedProfile is minimal on prefill). Used to
  // tell company vs non-company for the keep-as-credit option.
  const selectedProfile = (profiles || []).find((p) => p.id === form.customerId);

  // Platform (tama-app) customer = CustomerProfile has a linked User. Their refund ALWAYS
  // goes to their Wallet (the backend enforces it), so hide the bank / keep-as-credit
  // choices and show a wallet indicator instead.
  const isPlatformCustomer = !!(searchedProfile?.userId || selectedProfile?.userId);

  // Keep-as-credit needs at least one system order contributing to the refund —
  // the backend rejects it otherwise (the credit must be backed by cancelled orders).
  const hasLinkedOrders = form.productLinks.some((l) => l.orderId && itemAmount(l) > 0);
  // Keep-as-credit works for every reason. Without linked orders the required detail
  // note is the evidence behind the credited amount (enforced server-side too).
  const noteRequired = form.reason === 'other' || form.reason === 'admin_mistake' || (form.keepAsCredit && !hasLinkedOrders);

  // Reset the form each time the modal opens. With a prefill (e.g. from the
  // cancellation tab, or a customer-service case hand-off) the customer/order/amount
  // are pre-loaded. A case hand-off may have no customerId (no linked order) — in that
  // case we still seed the name/amount/reason as hints but leave the customer search
  // open so an admin picks (and confirms) the real profile before submitting.
  useEffect(() => {
    if (!isOpen) return;
    getCustomerProfiles();
    if (prefill && (prefill.customerId || prefill.smileName || prefill.contact)) {
      // Zip url+id so a link keeps its OWN order id — two cancelled orders with the same
      // URL must each resolve to their own order, not both to the first lookup match.
      const links = (prefill.orderUrls || [])
        .map((url, i) => ({ url, id: (prefill.orderIds || [])[i] }))
        .filter((x) => x.url);
      setForm({
        ...emptyForm(),
        customerId: prefill.customerId || null,
        smileName: prefill.smileName || '',
        reason: prefill.reason || 'cancelled',
        amount: prefill.amount ? String(prefill.amount) : '',
        productLinks: links.length ? links.map((x) => ({ ...emptyItem(), url: x.url })) : [emptyItem()],
      });
      setLinkStatuses({});
      setSearchedProfile(prefill.customerId ? { contact: prefill.contact || prefill.smileName || '', advanceAmount: 0 } : null);
      if (prefill.customerId) links.forEach((x, i) => handleLinkLookup(i, x.url, prefill.customerId, x.id));
    } else {
      setForm(emptyForm());
      setLinkStatuses({});
      setSearchedProfile(null);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleProfileSelect = async (smileName) => {
    if (!smileName) return;
    const matched = (profiles || []).find((p) => p.smileName === smileName);
    if (!matched) return; // ignore partial typing — only act on a real profile
    const profile = await searchCustomer(smileName);
    if (profile) {
      const sn = profile.smileName || smileName;
      setForm((prev) => ({
        ...prev,
        customerId: profile.id || matched.id,
        smileName: sn,
        // A case-ticket hand-off arrives with the amount the customer already
        // CONFIRMED — never silently replace it with the advance balance.
        amount: prefill?.amount
          ? prev.amount
          : (profile.advanceAmount > 0 ? String(profile.advanceAmount) : prev.amount),
        // Different customer, different eligibility — never carry the checkbox over
        // (a company customer would hide the checkbox but still submit keepAsCredit).
        keepAsCredit: false,
      }));
    }
  };

  const handleLinkChange = (idx, value) => {
    setForm((prev) => ({ ...prev, productLinks: prev.productLinks.map((l, i) => i === idx ? { ...emptyItem(), url: value } : l) }));
    setLinkStatuses((prev) => { const n = { ...prev }; delete n[idx]; return n; });
    // auto-search shortly after a link is pasted — fires for any URL (no domain whitelist)
    clearTimeout(lookupTimers.current[idx]);
    if (/^https?:\/\//i.test(value.trim()) && form.customerId) {
      lookupTimers.current[idx] = setTimeout(() => handleLinkLookup(idx, value), 500);
    }
  };

  const handleItemField = (idx, field, value) =>
    setForm((prev) => ({ ...prev, productLinks: prev.productLinks.map((l, i) => i === idx ? { ...l, [field]: value } : l) }));

  const toggleComponent = (idx, field) =>
    setForm((prev) => ({ ...prev, productLinks: prev.productLinks.map((l, i) => i === idx ? { ...l, [field]: !l[field] } : l) }));

  const addLinkField = () =>
    setForm((prev) => ({ ...prev, productLinks: [...prev.productLinks, emptyItem()] }));

  const removeLinkField = (idx) => {
    setForm((prev) => ({ ...prev, productLinks: prev.productLinks.filter((_, i) => i !== idx) }));
    setLinkStatuses((prev) => {
      const reindexed = {};
      Object.entries(prev).forEach(([k, v]) => {
        const n = parseInt(k);
        if (n < idx) reindexed[n] = v;
        else if (n > idx) reindexed[n - 1] = v;
      });
      return reindexed;
    });
  };

  const handleLinkLookup = async (idx, url, customerIdOverride, expectedOrderId) => {
    if (!url.trim()) return;
    const cid = customerIdOverride || form.customerId;
    if (!cid) { toast.error('กรุณาเลือกลูกค้าก่อนค้นหา Link'); return; }
    setLinkStatuses((prev) => ({ ...prev, [idx]: { loading: true } }));
    const data = await searchByUrl(url.trim(), cid);
    if (data && data.results && data.results.length > 0) {
      // Pick the right order:
      //  - prefill: the specific cancelled order this link came from (by id);
      //  - manual: the first matching order NOT already used by another link, so two
      //    identical URLs map to two DISTINCT orders (same link can be two orders with
      //    different options — each charged separately, refunded together).
      // Fall back to results[0] only when no unused match remains (e.g. same URL pasted
      // more times than there are orders).
      const usedOrderIds = new Set(
        formRef.current.productLinks.filter((l, i) => i !== idx && l.orderId).map((l) => l.orderId)
      );
      const row = (expectedOrderId && data.results.find((r) => r.orderId === expectedOrderId))
        || data.results.find((r) => !usedOrderIds.has(r.orderId))
        || data.results[0];
      // Duplicate: we had to reuse an order already on another link (same URL but only
      // one order exists) → likely a mistake. 2+ matching orders → distinct, allowed.
      const duplicate = !expectedOrderId && usedOrderIds.has(row.orderId);
      // Two refund systems, by customer type:
      //  • Platform (wallet) customer → refund the EXACT amount debited from their wallet
      //    (plain cost). The rate estimate is kept only to show side by side / flag a mismatch,
      //    and to fall back on if no wallet record exists.
      //  • Old customer → keep the rate estimate, and a card order is refunded with its 3.5%
      //    added back (isCreditCard). The wallet exact-charge path does not apply to them.
      const est = computeComponents(row);
      const estTotal = Math.round((est.productAmt + est.feeAmt) * 100) / 100;
      const isCard = row.paymentMethod === 'creditCard';
      // Decide platform-ness from the ROW's own customer (row.userId), NOT the component's
      // isPlatformCustomer: in prefill mode this lookup runs before the profile state has
      // loaded, so the component flag is still false here and would wrongly skip the exact
      // charge. A row with a userId is a platform/wallet customer whose chargedTotal is the
      // exact plain debit.
      const usePlatformExact = row.userId != null && row.chargedTotal != null;
      // Platform: refund the exact wallet charge as ONE amount (product + fee + everything).
      // Old: the estimate split into product / fee for the two component toggles.
      const productAmt = usePlatformExact ? row.chargedTotal : est.productAmt;
      const feeAmt = usePlatformExact ? 0 : est.feeAmt;
      setLinkStatuses((prev) => ({ ...prev, [idx]: { loading: false, order: row, duplicate } }));
      setForm((prev) => ({
        ...prev,
        productLinks: prev.productLinks.map((l, i) => i === idx
          ? { ...l, orderId: row.orderId, isManual: false, productAmt, feeAmt,
              estProductAmt: usePlatformExact ? estTotal : est.productAmt, estFeeAmt: est.feeAmt,
              hasActual: usePlatformExact,
              isCreditCard: usePlatformExact ? false : isCard,
              includeProduct: true, includeFee: feeAmt > 0, note: '' }
          : l),
      }));
    } else {
      // not found in system → let the user enter the amount + a required note by hand
      setLinkStatuses((prev) => ({ ...prev, [idx]: { loading: false, notFound: true } }));
      setForm((prev) => ({ ...prev, productLinks: prev.productLinks.map((l, i) => i === idx ? { ...l, orderId: null, isManual: true } : l) }));
    }
  };

  useEffect(() => {
    // Sum each order at most once — if the same order is on two link rows (e.g. the same
    // URL pasted twice), don't double-count its amount. Manual rows have no orderId.
    const total = sumRefundTotal(form.productLinks);
    if (total === 0) return;
    // Keep-as-credit: the customer's existing advanceAmount already IS store credit —
    // adding it into the kept amount would credit it to them a second time. It's only
    // included for a cash refund (transfer everything back in one go).
    const advanceAmt = searchedProfile && !form.keepAsCredit ? parseFloat(searchedProfile.advanceAmount || 0) : 0;
    // Round to 2 decimals to clear float noise while keeping satang precision.
    const grand = Math.round((total + advanceAmt) * 100) / 100;
    setForm((prev) => ({ ...prev, amount: String(grand) }));
    if (advanceAmt > 0) {
      toast.warning(`⚠️ ลูกค้ามียอดชำระเกิน ${advanceAmt.toLocaleString()} บาท รวมยอดคืนทั้งหมด ${grand.toLocaleString()} บาท`, { autoClose: 6000 });
    }
  }, [form.productLinks, form.keepAsCredit]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (isSubmitting) return; // a double-click must never create two refunds
    if (!form.customerId) { toast.error('กรุณาค้นหาและเลือกลูกค้าก่อน'); return; }
    if (!form.amount) { toast.error('กรุณากรอกยอดโอนคืน'); return; }
    if (!form.reason) { toast.error('กรุณาเลือกเหตุผลการโอนคืน'); return; }

    // a typed link must be either resolved to an order or switched to manual mode
    const hasUnchecked = form.productLinks.some((l) => l.url.trim() && !l.orderId && !l.isManual);
    if (hasUnchecked) { toast.error('มีรายการที่ยังไม่ได้ตรวจสอบ Link — กดค้นหาก่อน'); return; }

    // manual items need both an amount and a note
    const manualItems = form.productLinks.filter((l) => l.isManual && (l.url.trim() || l.amount));
    const badManual = manualItems.some((l) => !(parseFloat(l.amount) > 0) || !l.note.trim());
    if (badManual) { toast.error('รายการที่กรอกเอง ต้องระบุยอดและหมายเหตุให้ครบ'); return; }

    // The note is the evidence when no cancelled order backs the refund/credit.
    if (noteRequired && !form.reasonNote.trim()) {
      toast.error(form.reason === 'other' ? 'กรุณาระบุเหตุผล "อื่นๆ"' : 'กรุณาระบุรายละเอียด'); return;
    }

    if (form.accountNumber && !/^\d+$/.test(form.accountNumber)) {
      toast.error('เลขบัญชีต้องเป็นตัวเลขเท่านั้น'); return;
    }
    if (form.accountName && /^\d+$/.test(form.accountName.trim())) {
      toast.error('ชื่อบัญชีต้องเป็นชื่อ ไม่ใช่ตัวเลข — กรุณาตรวจสอบว่าไม่ได้กรอกสลับช่อง'); return;
    }

    // Only link orders that actually contribute to this refund. An order whose components
    // are all toggled off (itemAmount 0) is NOT refunded, so it must not be flipped to
    // 'คืนเงินแล้ว' at transfer. De-duped so the same order never appears twice.
    const orderIds = [...new Set(form.productLinks.filter((l) => l.orderId && itemAmount(l) > 0).map((l) => l.orderId))];

    const hasManualItems = manualItems.length > 0;
    const manualNote = manualItems
      .map((l) => `กรอกเอง: ${l.note.trim()} (${formatAmount(parseFloat(l.amount))} บาท)`)
      .join(' | ');
    const reasonNote =
      form.reason === 'other' && form.reasonNote ? `อื่นๆ: ${form.reasonNote}`
      : form.reason === 'admin_mistake' && form.reasonNote ? `สรุปผิด: ${form.reasonNote}`
      : form.reasonNote ? `รายละเอียด: ${form.reasonNote}`
      : '';
    const note = [reasonNote, manualNote].filter(Boolean).join(' | ') || null;

    setIsSubmitting(true);
    try {
      const created = await createRecord({
        customerId: form.customerId,
        smileName: form.smileName,
        amount: parseFloat(form.amount),
        orderIds,
        hasManualItems,
        bankName: form.keepAsCredit ? '' : form.bankName,
        accountNumber: form.keepAsCredit ? '' : form.accountNumber,
        accountName: form.keepAsCredit ? '' : form.accountName,
        reason: form.reason,
        note,
        keepAsCredit: form.keepAsCredit,
      });
      if (created) {
        toast.success(form.keepAsCredit ? 'เก็บเป็นยอดฝากไว้ให้ลูกค้าแล้ว' : 'สร้างรายการโอนเงินคืนสำเร็จ');
        onCreated?.(created);
        onClose?.();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} style={modalStyles} shouldCloseOnOverlayClick={false}>
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-2xl font-bold">สร้างรายการโอนเงินคืนใหม่</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-3xl leading-none">×</button>
      </div>

      {/* Customer */}
      <div className="mb-5 p-4 bg-blue-50 rounded-lg border border-blue-100">
        <p className="text-sm font-semibold text-blue-800 mb-2">
          {lockCustomer ? 'ลูกค้า (จากรายการที่ยกเลิก)' : 'ค้นหาลูกค้าเพื่อดึงข้อมูลอัตโนมัติ'}
        </p>
        {lockCustomer ? (
          <p className="font-semibold text-green-700 text-sm">
            ✓ {prefill?.contact || searchedProfile?.contact || '—'}{prefill?.smileName ? ` (${prefill.smileName})` : ''}
          </p>
        ) : (
          <>
            <ProfileCombo profiles={profiles || []} onChange={handleProfileSelect} initialSmileName={form.smileName} />
            {searchedProfile && (
              <div className="mt-3 p-3 bg-white rounded border border-blue-200 text-sm space-y-1">
                <p className="font-semibold text-green-700">✓ พบลูกค้า: {searchedProfile.contact}</p>
                {searchedProfile.advanceAmount > 0 && (
                  <p className="text-orange-700">ยอดชำระเกินมา: <span className="font-bold">{formatAmount(searchedProfile.advanceAmount)} บาท</span> <span className="text-gray-400">(กรอกลงยอดโอนคืนแล้ว)</span></p>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Reason */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">เหตุผลการโอนคืน <span className="text-red-500">*</span></label>
        <div className="grid grid-cols-3 gap-2 mb-2">
          {REASONS.map((r) => (
            <button key={r.value} type="button"
              onClick={() => setForm((prev) => ({ ...prev, reason: r.value, reasonNote: '' }))}
              className={`py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                form.reason === r.value
                  ? 'border-orange-500 bg-orange-50 text-orange-700'
                  : 'border-gray-200 text-gray-600 hover:border-gray-400'
              }`}>
              {r.label}
            </button>
          ))}
        </div>
        {noteRequired && (
          <input type="text"
            placeholder={
              form.reason === 'admin_mistake' ? 'ระบุรายละเอียดที่สรุปผิด เช่น คำนวณค่าส่งเกิน 70 บาท *'
              : form.reason === 'other' ? 'ระบุเหตุผล *'
              : 'ระบุรายละเอียด (จำเป็นเมื่อเก็บเป็นยอดฝากโดยไม่มีรายการอ้างอิง) *'
            }
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={form.reasonNote}
            onChange={(e) => setForm((prev) => ({ ...prev, reasonNote: e.target.value }))} />
        )}
      </div>

      {/* Items — product links with auto-lookup */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">รายการสินค้า / Link <span className="font-normal text-gray-400">(กดป้ายเพื่อเลือก/ตัดออก)</span></label>
          {(() => {
            const total = form.productLinks.reduce((s, l) => s + itemAmount(l), 0);
            return total > 0 ? (
              <span className="text-sm font-semibold text-blue-700">รวมรายการ: {formatAmount(total)} บาท</span>
            ) : null;
          })()}
        </div>
        {form.productLinks.map((link, idx) => {
          const st = linkStatuses[idx] || {};
          const order = st.order;
          const prodMismatch = link.hasActual && amtDiffers(link.productAmt, link.estProductAmt);
          const feeMismatch = link.hasActual && amtDiffers(link.feeAmt, link.estFeeAmt);
          return (
            <div key={idx} className="mb-3 border border-gray-100 rounded-lg p-2">
              <div className="flex gap-2">
                <input type="text" placeholder={`Link สินค้า ${idx + 1}`}
                  className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm"
                  value={link.url}
                  onChange={(e) => handleLinkChange(idx, e.target.value)} />
                <button onClick={() => handleLinkLookup(idx, link.url)} disabled={!link.url.trim() || st.loading}
                  className="bg-gray-100 hover:bg-gray-200 disabled:opacity-40 px-3 rounded border border-gray-300 text-sm">
                  {st.loading ? '...' : '🔍'}
                </button>
                {form.productLinks.length > 1 && (
                  <button onClick={() => removeLinkField(idx)} className="text-red-400 hover:text-red-600 px-2 text-lg">✕</button>
                )}
              </div>

              {/* found in system → selectable paid components + item subtotal. Each component
                  shows the EXACT charged figure (used) next to the rate estimate; a mismatch
                  beyond a satang is flagged so a bad amount from another flow is visible. */}
              {order && (
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs">
                  {/* product component — toggle to include/exclude */}
                  <button type="button" onClick={() => toggleComponent(idx, 'includeProduct')}
                    className={`px-2 py-0.5 rounded border transition-all ${link.includeProduct
                      ? (PRODUCT_PAYMENT_STYLE[order.productPayment] || 'bg-blue-50 text-blue-700 border-blue-300')
                      : 'bg-gray-50 text-gray-400 border-gray-200 line-through'}`}>
                    {link.includeProduct ? '✓' : '✗'} 🛍️ ค่าสินค้า: {formatAmount(link.productAmt)} <span className="opacity-70">({order.productPayment || '—'})</span>
                  </button>
                  {/* Exact wallet charge is used. Only surface the estimate when it disagrees
                      by more than rounding (prodMismatch = hasActual && >฿0.05) — otherwise
                      hide it, since a satang gap is just how the charge is summed. */}
                  {prodMismatch && (
                    <span className="text-amber-600 font-medium">
                      ⚠️ ประเมิน {formatAmount(link.estProductAmt)}
                    </span>
                  )}
                  {order.userId != null && !link.hasActual && (
                    <span className="text-amber-600">≈ ยอดประเมิน (ไม่พบยอดชำระจริงในกระเป๋าเงิน)</span>
                  )}
                  {/* fee component (ค่ากด + ค่าบริการส่งตรวจ) — shown whenever something was
                      actually paid (feeAmt > 0). Deliberately NOT gated on transactionFeePaid:
                      feeAmt now includes a paid ส่งตรวจ fee, and includeFee turns on whenever
                      feeAmt > 0, so an order with a paid ส่งตรวจ fee and no transaction fee
                      would otherwise be refunded a fee the admin could neither see nor untick. */}
                  {link.feeAmt > 0 && (
                    <>
                      <button type="button" onClick={() => toggleComponent(idx, 'includeFee')}
                        className={`px-2 py-0.5 rounded border transition-all ${link.includeFee
                          ? 'bg-green-50 text-green-700 border-green-300'
                          : 'bg-gray-50 text-gray-400 border-gray-200 line-through'}`}>
                        {link.includeFee ? '✓' : '✗'} 🧾 ค่ากด{order.extraJpFeesPaid && Array.isArray(order.extraJpFees) && order.extraJpFees.length > 0 ? '+ส่งตรวจ' : ''}: {formatAmount(link.feeAmt)}
                      </button>
                      {feeMismatch && (
                        <span className="text-amber-600 font-medium">
                          ⚠️ ประเมิน {formatAmount(link.estFeeAmt)}
                        </span>
                      )}
                    </>
                  )}
                  {order.isCredit && (
                    <span className="px-2 py-0.5 rounded border bg-purple-50 text-purple-700 border-purple-200">💳 เครดิต</span>
                  )}
                  {/* Old-customer credit-card order — refunded with the 3.5% card fee added back. */}
                  {link.isCreditCard && (
                    <span className="px-2 py-0.5 rounded border bg-orange-50 text-orange-700 border-orange-200">💳 บัตรเครดิต +3.5%</span>
                  )}
                  <span className="ml-auto font-semibold text-blue-700">รวมรายการนี้: {formatAmount(itemAmount(link))} บาท</span>
                </div>
              )}

              {/* same URL but only one order exists → likely a mistake; not double-counted */}
              {st.duplicate && (
                <p className="mt-1.5 text-xs text-amber-600">
                  ⚠️ ลิงก์นี้ซ้ำกับรายการอื่น (มีออเดอร์เดียว) — อาจกรอกผิด จะไม่ถูกนับซ้ำ
                </p>
              )}

              {/* not found → manual amount + required note */}
              {link.isManual && (
                <div className="mt-1.5 p-2 bg-yellow-50 border border-yellow-200 rounded space-y-2">
                  <p className="text-xs text-yellow-700">⚠️ ไม่พบในระบบ — กรอกยอดคืนและหมายเหตุเอง</p>
                  <div className="flex gap-2">
                    <input type="text" inputMode="decimal" placeholder="ยอดคืน (บาท)"
                      className="w-32 border border-gray-300 rounded px-2 py-1.5 text-sm"
                      value={link.amount}
                      onChange={(e) => handleItemField(idx, 'amount', e.target.value.replace(/[^\d.]/g, ''))} />
                    <input type="text" placeholder="หมายเหตุ (จำเป็น) *"
                      className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm"
                      value={link.note}
                      onChange={(e) => handleItemField(idx, 'note', e.target.value)} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
        <button onClick={addLinkField} className="text-blue-600 hover:text-blue-800 text-sm font-medium">+ เพิ่มรายการ</button>
      </div>

      {/* Amount */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          ยอดโอนคืน (บาท) <span className="text-red-500">*</span>
          <span className="ml-2 font-normal text-blue-600">✎ แก้ไขได้ — คำนวณจากรายการด้านบนให้แล้ว ปรับยอดเองได้</span>
        </label>
        {/* text + inputMode so the value can't change on mouse-wheel scroll (type=number bug) */}
        <input type="text" inputMode="decimal" className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          value={form.amount}
          onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value.replace(/[^\d.]/g, '') }))} />
      </div>

      {/* Keep-as-credit: store the amount as the customer's advanceAmount instead of a
          cash refund. Only for customers who don't pay via link (non-company). Company
          customers pay via link, so show a note explaining why the option is unavailable. */}
      {/* Platform (wallet) customer → refund goes to their wallet automatically. */}
      {form.customerId && isPlatformCustomer && (
        <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm text-purple-800 font-medium">
          💰 คืนเข้ากระเป๋าเงินลูกค้า (Wallet) โดยอัตโนมัติ — ลูกค้าจ่ายผ่านกระเป๋าเงิน จึงไม่ต้องเลือกธนาคารหรือเก็บยอดฝาก
        </div>
      )}

      {!isPlatformCustomer && form.customerId && selectedProfile && (selectedProfile.isCompanyCustomer ? (
        <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-500">
          ℹ️ "เก็บเป็นยอดฝากไว้" ใช้ได้เฉพาะลูกค้าที่ไม่ได้จ่ายผ่านลิ้งค์
        </div>
      ) : (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <label className="flex items-center gap-2 text-sm font-medium text-amber-900 cursor-pointer">
            <input
              type="checkbox"
              checked={form.keepAsCredit}
              onChange={(e) => setForm((prev) => ({ ...prev, keepAsCredit: e.target.checked }))} />
            เก็บเป็นยอดฝากไว้แทนการโอนคืน (ไม่ต้องโอน — เพิ่มเข้ายอดฝากของลูกค้า)
          </label>
          {form.keepAsCredit && !hasLinkedOrders && (
            <p className="text-xs text-amber-700 mt-1">ไม่มีรายการยกเลิกอ้างอิง — ต้องระบุรายละเอียดในช่องเหตุผลด้านบน</p>
          )}
        </div>
      ))}

      {/* Bank info — manual flow only. In the cancel flow we inform the customer
          first and collect bank details later in the "รอข้อมูลบัญชี" step. */}
      {!isPlatformCustomer && !lockCustomer && !form.keepAsCredit && (
      <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-sm font-semibold text-gray-700 mb-3">ข้อมูลธนาคาร <span className="font-normal text-gray-400">(ไม่บังคับ)</span></p>
        <div className="grid grid-cols-5 gap-2 mb-3">
          {BANKS.map((bank) => (
            <BankLogoButton key={bank.name} bank={bank}
              selected={form.bankName === bank.name}
              onClick={() => setForm((prev) => ({ ...prev, bankName: prev.bankName === bank.name ? '' : bank.name }))} />
          ))}
        </div>
        {form.bankName && <p className="text-xs text-blue-700 mb-2">เลือก: {form.bankName}</p>}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">เลขบัญชี</label>
            <input type="text" placeholder="ตัวเลขเท่านั้น" inputMode="numeric"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              value={form.accountNumber}
              onChange={(e) => setForm((prev) => ({ ...prev, accountNumber: e.target.value.replace(/\D/g, '') }))} />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">ชื่อบัญชี</label>
            <input type="text" placeholder="ชื่อ-นามสกุล"
              className={`w-full border rounded px-3 py-2 text-sm ${/^\d+$/.test(form.accountName.trim()) ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
              value={form.accountName}
              onChange={(e) => setForm((prev) => ({ ...prev, accountName: e.target.value }))} />
            {/^\d+$/.test(form.accountName.trim()) && (
              <p className="text-xs text-red-500 mt-0.5">ชื่อบัญชีไม่ควรเป็นตัวเลข</p>
            )}
          </div>
        </div>
      </div>
      )}

      <div className="flex justify-end gap-3">
        <button onClick={onClose}
          className="px-5 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50">ยกเลิก</button>
        <button onClick={handleSubmit} disabled={isSubmitting}
          className="px-5 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed">
          {isSubmitting ? 'กำลังบันทึก...' : (form.keepAsCredit ? 'เก็บเป็นยอดฝากไว้' : 'บันทึก')}</button>
      </div>
    </Modal>
  );
};

export default RefundCreateModal;
